import { apiRequest, queryString } from '../../lib/api';
import {
  USER_PASSWORD_RESET_CONFIRMATION,
  parseSystemSettingValue,
  validateCreateAdminUserInput,
  validateUserPasswordResetInput,
  type AdjustOcCreditInput,
  type AuditFilters,
  type AuditLogRow,
  type CreateAdminUserInput,
  type CreateFranchiseInput,
  type CreateStoreInput,
  type FranchiseFilters,
  type FranchiseRow,
  type IssueOcCreditInput,
  type OcFilters,
  type OcMutationResult,
  type OcRequestReviewInput,
  type OcRequestRow,
  type OcRequestStatusFilter,
  type OcTransactionRow,
  type PaginatedResult,
  type ResetUserPasswordInput,
  type SettingFilters,
  type SetFranchiseStatusInput,
  type StoreFilters,
  type StoreRow,
  type SuspendUserInput,
  type SystemDashboardReadModel,
  type SystemSettingRow,
  type UnsuspendUserInput,
  type UpdateFranchiseInput,
  type UpdateStoreInput,
  type UpdateSystemSettingInput,
  type UserFilters,
  type UserRow,
} from './data';

export const cloudflareSystemData = {
  dashboard: () => apiRequest<SystemDashboardReadModel>('/api/v1/system/dashboard'),
  franchises: (filters: FranchiseFilters, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<FranchiseRow>>(
      `/api/v1/system/franchises${queryString({ ...filters, cursor, limit })}`,
    ),
  stores: (filters: StoreFilters, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<StoreRow>>(
      `/api/v1/system/stores${queryString({
        search: filters.search,
        low_oc_only: filters.lowOcOnly,
        cursor,
        limit,
      })}`,
    ),
  users: (filters: UserFilters, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<UserRow>>(
      `/api/v1/system/users${queryString({ ...filters, cursor, limit })}`,
    ),
  ocTransactions: (filters: OcFilters, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<OcTransactionRow>>(
      `/api/v1/system/oc-transactions${queryString({
        transaction_type: filters.transactionType,
        cursor,
        limit,
      })}`,
    ),
  ocRequests: (status: OcRequestStatusFilter, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<OcRequestRow>>(
      `/api/v1/system/oc-requests${queryString({ status, cursor, limit })}`,
    ),
  approveOcRequest: (input: OcRequestReviewInput) =>
    apiRequest<{ request_id: string; status: 'approved'; new_balance: number; amount: number }>(
      `/api/v1/system/oc-requests/${input.id}/approve`,
      {
        method: 'POST',
        idempotent: true,
        body: JSON.stringify(input.reviewReason.trim() ? { review_reason: input.reviewReason.trim() } : {}),
      },
    ),
  rejectOcRequest: (input: OcRequestReviewInput) =>
    apiRequest<{ request_id: string; status: 'rejected'; review_reason: string }>(
      `/api/v1/system/oc-requests/${input.id}/reject`,
      {
        method: 'POST',
        idempotent: true,
        body: JSON.stringify({ review_reason: input.reviewReason.trim() }),
      },
    ),
  auditLogs: (filters: AuditFilters, cursor: string | null, limit = 50) =>
    apiRequest<PaginatedResult<AuditLogRow>>(
      `/api/v1/system/audit-logs${queryString({
        search: filters.search,
        target_type: filters.targetType,
        cursor,
        limit,
      })}`,
    ),
  settings: (filters: SettingFilters) =>
    apiRequest<PaginatedResult<SystemSettingRow>>(
      `/api/v1/system/settings${queryString({ category: filters.category })}`,
    ).then((result) => ({ ...result, hasMore: false, nextCursor: null })),
  createFranchise: (input: CreateFranchiseInput) =>
    apiRequest<FranchiseRow>('/api/v1/system/franchises', {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        name: input.name.trim(),
        region: input.region.trim() || null,
        commission_rate: input.commissionRate,
        exchange_rate: input.exchangeRate,
        oc_balance: input.ocBalance,
      }),
    }),
  updateFranchise: (input: UpdateFranchiseInput) =>
    apiRequest<FranchiseRow>(`/api/v1/system/franchises/${input.id}`, {
      method: 'PATCH',
      idempotent: true,
      body: JSON.stringify({
        name: input.name.trim(),
        region: input.region.trim() || null,
        commission_rate: input.commissionRate,
        exchange_rate: input.exchangeRate,
      }),
    }),
  setFranchiseStatus: (input: SetFranchiseStatusInput) =>
    apiRequest<FranchiseRow>(`/api/v1/system/franchises/${input.id}/status`, {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        status: input.status,
        confirmation_text: input.confirmationText.trim(),
        reason: input.reason.trim(),
      }),
    }),
  createStore: (input: CreateStoreInput) =>
    apiRequest<StoreRow>('/api/v1/system/stores', {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        name: input.name.trim(),
        franchise_id: input.franchiseId.trim() || null,
        krw_per_1000_points: input.krwPer1000Points,
        oc_balance: input.ocBalance,
      }),
    }),
  updateStore: (input: UpdateStoreInput) =>
    apiRequest<StoreRow>(`/api/v1/system/stores/${input.id}`, {
      method: 'PATCH',
      idempotent: true,
      body: JSON.stringify({
        name: input.name.trim(),
        franchise_id: input.franchiseId.trim() || null,
        krw_per_1000_points: input.krwPer1000Points,
      }),
    }),
  setUserStatus: (input: SuspendUserInput | UnsuspendUserInput, status: 'active' | 'suspended') =>
    apiRequest<UserRow>(`/api/v1/system/users/${input.userId}/status`, {
      method: 'PATCH',
      idempotent: true,
      body: JSON.stringify({
        status,
        reason: input.reason.trim(),
        duration: 'duration' in input ? input.duration.trim() || null : null,
      }),
    }),
  resetPassword: (input: ResetUserPasswordInput) => {
    const error = validateUserPasswordResetInput(input);
    if (error) return Promise.reject(new Error(error));
    return apiRequest<void>(`/api/v1/system/users/${input.userId}/reset-password`, {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        new_password: input.newPassword,
        confirmation_text: USER_PASSWORD_RESET_CONFIRMATION,
        reason: input.reason.trim(),
        source: input.source ?? 'system_admin_react_cloudflare',
      }),
    });
  },
  createAdminUser: (input: CreateAdminUserInput) => {
    const error = validateCreateAdminUserInput(input);
    if (error) return Promise.reject(new Error(error));
    return apiRequest<UserRow>('/api/v1/system/users', {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        role: input.role,
        username: input.username.trim(),
        password: input.password,
        nickname: input.nickname.trim(),
        staff_phone_number: input.staffPhoneNumber.trim() || null,
        franchise_id: input.role === 'super_admin' ? input.franchiseId : null,
        store_id: input.role === 'staff_admin' ? input.storeId : null,
        reason: input.reason.trim(),
        source: input.source ?? 'system_admin_react_cloudflare',
      }),
    });
  },
  issueOc: (input: IssueOcCreditInput) =>
    apiRequest<OcMutationResult>('/api/v1/system/oc/issue', {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        franchise_id: input.franchiseId,
        amount: input.amount,
        amount_krw: input.amountKrw,
        reason: input.reason.trim(),
      }),
    }),
  adjustOc: (input: AdjustOcCreditInput) =>
    apiRequest<OcMutationResult>('/api/v1/system/oc/adjust', {
      method: 'POST',
      idempotent: true,
      body: JSON.stringify({
        franchise_id: input.franchiseId,
        amount: input.amount,
        reason: input.reason.trim(),
      }),
    }),
  updateSetting: (input: UpdateSystemSettingInput) =>
    apiRequest<SystemSettingRow>(`/api/v1/system/settings/${input.id}`, {
      method: 'PATCH',
      idempotent: true,
      body: JSON.stringify({
        value: parseSystemSettingValue(input.valueText, input.valueType),
        confirmation_text: input.confirmationText.trim(),
        reason: input.reason.trim(),
      }),
    }),
};
